
import React, { useState, useEffect } from 'react';
import { ArrowRight, Award, Users, Calendar, Sword, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import LazyYouTube from '@/components/LazyYouTube';

const Home = () => {
  const [hoveredWeapon, setHoveredWeapon] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState<string | null>(null);
  const [showNonCriticalContent, setShowNonCriticalContent] = useState(false);

  const weaponVideos = {
    florett: 'g-P_q5gdvRM',
    degen: '88mBQ2u2Wb0', 
    saebel: '4ipiJFlU_Dg'
  };

  // Defer non-critical content to improve TBT
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNonCriticalContent(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const getVideoId = (url: string) => {
    const match = url.match(/youtu\.be\/([^?]+)/);
    return match ? match[1] : '';
  };

  return (
    <div className="relative">
      {/* Hero Section with fencing image and misty overlay */}
      <section className="hero-section">
        {/* LCP-optimized hero image */}
        <img 
          src="/lovable-uploads/08f66298-356b-4d6b-91dd-90d5acd9fb03.png"
          alt=""
          className="hero-bg"
          loading="eager"
          fetchPriority="high"
          width="1920"
          height="1080"
          decoding="sync"
        />
        
        {/* Optimized overlay gradients */}
        <div className="hero-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-slate-900/30"></div>

        <div className="hero-content">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 bg-clip-text text-transparent">
            ASC Fechten Wien
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed drop-shadow-lg">
            Tradition trifft Moderne - Erlebe die Eleganz und Dynamik des Fechtsports 
            im Herzen Wiens
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/training"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Jetzt anmelden
              <ArrowRight className="ml-2" size={20} />
            </Link>
            
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-4 border-2 border-amber-400 text-amber-400 font-semibold rounded-lg hover:bg-amber-400 hover:text-slate-900 transition-all duration-300 backdrop-blur-sm bg-slate-900/30"
            >
              Mehr erfahren
            </Link>
          </div>
        </div>
      </section>

      {/* Regeln des Fechtens Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">Die Regeln des Fechtens</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Fechten ist mehr als nur Kampf - es ist ein Sport mit klaren Regeln, 
              der Technik, Taktik und mentale Stärke vereint.
            </p>
          </div>

          <div className="space-y-12">
            {/* Video */}
            <div className="max-w-4xl mx-auto">
              <LazyYouTube
                videoId="x7zxpDW8nb0"
                title="Die Regeln des Fechtens"
              />
            </div>

            {/* Regeln */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-900/50 rounded-2xl p-8 border border-amber-500/20">
                <h3 className="text-2xl font-bold text-amber-400 mb-6 text-center">Die wichtigsten Grundregeln</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🎯 Trefferzonen</h4>
                    <p className="text-slate-300 text-sm">
                      Je nach Waffe gelten unterschiedliche Trefferzonen: Florett (Rumpf), 
                      Degen (ganzer Körper), Säbel (Oberkörper mit Armen).
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">⚡ Vortrittrecht</h4>
                    <p className="text-slate-300 text-sm">
                      Bei Florett und Säbel entscheidet das Vortrittrecht, wer bei einem Doppeltreffer 
                      den Punkt erhält. Wer zuerst angreift, hat Vorrang.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🏆 Punktesystem</h4>
                    <p className="text-slate-300 text-sm">
                      Ein Gefecht geht bis 15 Treffer (bei Turnieren) oder bis 5 Treffer (Training). 
                      Maximale Kampfzeit: 9 Minuten (3×3 Min.).
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🛡️ Sicherheit</h4>
                    <p className="text-slate-300 text-sm">
                      Vollständige Schutzausrüstung ist Pflicht: Maske, Jacke, Handschuh, 
                      Unterziehjacke und bei Säbel zusätzlich Maskenkordel.
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">⚔️ Fair Play</h4>
                    <p className="text-slate-300 text-sm">
                      Respekt vor dem Gegner, dem Schiedsrichter und den Regeln steht im Mittelpunkt. 
                      Gruß vor und nach jedem Gefecht ist Tradition.
                    </p>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <p className="text-amber-400 font-semibold text-sm text-center">
                    💡 Tipp: Alle Regeln lernst du Schritt für Schritt in unserem Training. 
                    Unsere erfahrenen Trainer führen dich behutsam in die Welt des Fechtsports ein.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Die drei Waffen Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900/60 via-slate-800/40 to-slate-900/60">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">Die drei Waffen des Fechtsports</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Jede Waffe hat ihre eigene Tradition, Technik und Taktik. 
              Entdecke die Vielfalt des modernen Fechtsports.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Florett */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-amber-500/20 group hover:border-amber-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(251, 191, 36, 0.2) 70%, rgba(245, 158, 11, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('florett')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('florett')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/44ff05f7-8e8d-4c3e-aa2b-6d2cc9b8f29b.png" 
                    alt="Florett" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {hoveredWeapon === 'florett' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-amber-500 rounded-full p-4 hover:bg-amber-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-amber-400 mb-4">Florett</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    Das Florett ist die klassische Ausbildungswaffe des Fechtsports. 
                    Hierbei darf nur der Rumpf getroffen werden.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• Gewicht: ca. 500g</li>
                    <li>• Klinge: flexibel, rechteckiger Querschnitt</li>
                    <li>• Trefferfläche: Rumpf</li>
                    <li>• Vortrittrecht: Ja</li>
                  </ul>
                  <p className="text-amber-400 font-semibold">
                    Ideal für Einsteiger - Eleganz und Präzision stehen im Vordergrund
                  </p>
                </div>
              </div>
            </div>

            {/* Degen */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-blue-500/20 group hover:border-blue-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(59, 130, 246, 0.2) 70%, rgba(37, 99, 235, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('degen')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('degen')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/62dfff47-7630-4881-9fbf-54fb5933644a.png" 
                    alt="Degen" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {hoveredWeapon === 'degen' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-blue-500 rounded-full p-4 hover:bg-blue-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-blue-400 mb-4">Degen</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    Der Degen ist die schwerste Waffe. Der gesamte Körper ist Trefferfläche, 
                    was taktische Vielfalt ermöglicht.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• Gewicht: ca. 770g</li>
                    <li>• Klinge: steif, dreieckiger Querschnitt</li>
                    <li>• Trefferfläche: Ganzer Körper</li>
                    <li>• Vortrittrecht: Nein</li>
                  </ul>
                  <p className="text-blue-400 font-semibold">
                    Für Strategen - Geduld und Timing entscheiden über Sieg und Niederlage
                  </p>
                </div>
              </div>
            </div>

            {/* Säbel */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-green-500/20 group hover:border-green-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(34, 197, 94, 0.2) 70%, rgba(22, 163, 74, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('saebel')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('saebel')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/bd125961-9730-40e5-aa9b-8e4f2d3db84c.png" 
                    alt="Säbel" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {hoveredWeapon === 'saebel' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-green-500 rounded-full p-4 hover:bg-green-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-green-400 mb-4">Säbel</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    Der Säbel ist die dynamischste Waffe. Hieb- und Stoßtreffer sind möglich, 
                    was zu spektakulären Gefechten führt.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• Gewicht: ca. 500g</li>
                    <li>• Klinge: flexibel, rechteckiger Querschnitt</li>
                    <li>• Trefferfläche: Oberkörper</li>
                    <li>• Vortrittrecht: Ja</li>
                  </ul>
                  <p className="text-green-400 font-semibold">
                    Für Tempofechter - Schnelligkeit und Aggressivität sind entscheidend
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-300 mb-6">
              Interessiert? Probiere alle drei Waffen in unserem Schnuppertraining aus!
            </p>
            <Link
              to="/training"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105"
              onClick={() => window.scrollTo(0, 0)}
            >
              Zum Schnuppertraining
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">Warum ASC Fechten Wien?</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Seit 1970 fördern wir den Fechtsport mit professionellem Training, 
              moderner Ausstattung und einer starken Gemeinschaft
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Sword className="text-amber-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-amber-400 mb-3">Professionelles Training</h3>
              <p className="text-slate-300">Qualifizierte Trainer für alle Leistungsstufen</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="text-blue-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-blue-400 mb-3">Starke Gemeinschaft</h3>
              <p className="text-slate-300">Über 100 aktive Mitglieder aller Altersgruppen</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award className="text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-green-400 mb-3">Erfolgreiche Turniere</h3>
              <p className="text-slate-300">Regelmäßige Teilnahme an nationalen Wettkämpfen</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="text-purple-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">Flexible Zeiten</h3>
              <p className="text-slate-300">Training 4x wöchentlich für maximale Flexibilität</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600/10 to-blue-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-amber-400 mb-6">Bereit für dein erstes Training?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Egal ob Anfänger oder Fortgeschrittener - bei uns findest du das passende Training. 
            Komm vorbei und entdecke die Faszination des Fechtsports!
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105"
            onClick={() => window.scrollTo(0, 0)}
          >
            Kontakt aufnehmen
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-lg overflow-hidden max-w-4xl w-full relative">
            <button
              onClick={() => setShowVideo(null)}
              className="absolute top-4 right-4 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
              aria-label="Video schließen"
            >
              <X size={20} />
            </button>
            <div className="aspect-video">
              <LazyYouTube
                videoId={weaponVideos[showVideo as keyof typeof weaponVideos]}
                title="Fencing Video"
                autoplay={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
